import { useId } from 'react';
import { LazyMotion, domAnimation, m, useReducedMotion } from 'motion/react';
import type { PaymentStatus } from '../types';
import { ConfettiBurst } from './ConfettiBurst';
import { Icon } from './Icon';
import { QrCard } from './QrCard';
import { SuccessPassMark } from './SuccessPassMark';
import './registrationExperience.css';

interface RegistrationSuccessExperienceProps {
  registrationCode: string;
  qrToken?: string;
  participantName?: string;
  paymentStatus?: PaymentStatus;
  team?: { name: string; color: string } | null;
  celebrate?: boolean;
  paymentBusy?: boolean;
  onContinuePayment?: () => void;
  onShare?: () => void;
  onNewRegistration: () => void;
}

function paymentWasCompleted(status: PaymentStatus): boolean {
  return status === 'paid' || status === 'manual_free';
}

export function RegistrationSuccessExperience({
  registrationCode,
  qrToken,
  participantName,
  paymentStatus = 'pending_payment',
  team,
  celebrate = false,
  paymentBusy = false,
  onContinuePayment,
  onShare,
  onNewRegistration,
}: RegistrationSuccessExperienceProps): JSX.Element {
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  const paid = paymentWasCompleted(paymentStatus);
  const firstName = participantName?.trim().split(/\s+/u)[0];

  return (
    <LazyMotion features={domAnimation} strict>
      <ConfettiBurst active={celebrate} />
      <m.section
        className="fte-registration-success"
        aria-labelledby={titleId}
        initial={reduceMotion ? false : { opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduceMotion ? { duration: 0 } : { duration: 0.34, ease: 'easeOut' }}
      >
        <header className="fte-registration-success__hero">
          <SuccessPassMark />
          <div className="fte-registration-success__copy" role="status" aria-live="polite">
            <span className="eyebrow eyebrow--glow">INSCRIÇÃO CONFIRMADA</span>
            <h1 id={titleId}>Sua inscrição está confirmada!</h1>
            <p>
              {firstName ? `${firstName}, seu` : 'Seu'} passe para o 12 HORAS COM FORTEENS já está pronto.
              Guarde o QR Code para apresentar na entrada.
            </p>
          </div>
        </header>

        <div className="fte-registration-success__status">
          <span className={paid ? 'is-paid' : 'is-unpaid'}>
            <Icon name={paid ? 'check' : 'warning'} />
            {paid ? 'Pagamento realizado' : 'Pagamento não realizado'}
          </span>
          <code aria-label={`Código da inscrição ${registrationCode}`}>{registrationCode}</code>
          {team ? (
            <span className="fte-registration-success__team">
              <i style={{ backgroundColor: team.color }} aria-hidden="true" />
              {team.name}
            </span>
          ) : null}
        </div>

        {qrToken ? (
          <QrCard token={qrToken} code={registrationCode} />
        ) : (
          <div className="fte-registration-success__qr-loading" role="status" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            <div>
              <strong>Preparando seu QR Code</strong>
              <small>Seu código de inscrição já está salvo.</small>
            </div>
          </div>
        )}

        {!paid ? (
          <div className="fte-registration-success__payment-note">
            <Icon name="external" aria-hidden="true" />
            <div>
              <strong>Inscrição realizada com sucesso!</strong>
              <p>Continue pela InfinitePay para finalizar esta etapa da inscrição.</p>
            </div>
          </div>
        ) : null}

        <div className="fte-registration-success__actions">
          {!paid && onContinuePayment ? (
            <button type="button" className="button button--primary" disabled={paymentBusy} onClick={onContinuePayment}>
              {paymentBusy ? 'Abrindo pagamento…' : 'Continuar para o pagamento'} <Icon name="arrow-right" />
            </button>
          ) : null}
          {onShare ? (
            <button type="button" className={`button ${paid ? 'button--primary' : 'button--ghost'}`} onClick={onShare}>
              <Icon name="share" /> Chama a galera
            </button>
          ) : null}
          <button type="button" className="button button--ghost" onClick={onNewRegistration}>
            <Icon name="plus" /> Fazer nova inscrição
          </button>
        </div>
      </m.section>
    </LazyMotion>
  );
}
